import React, { useMemo } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  Badge,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UserAvatar from './UserAvatar';
import { stripHtmlTags } from '../utils/textUtils';

function GroupList({ 
  groups, 
  messages, 
  currentUserId, 
  selectedGroupId, 
  onSelectGroup, 
  lastReadTimestamps,
  onCreateGroupClick 
}) {
  // Sort groups by last message time
  const sortedGroups = useMemo(() => {
    const groupsWithLastMessage = groups.map(group => {
      // Find last message in this group
      const groupMessages = messages.filter(msg => 
        msg.conversationId === group.conversationId && msg.type === 'group'
      );
      const lastMessage = groupMessages.length > 0 
        ? groupMessages[groupMessages.length - 1]
        : null;

      // Calculate unread count
      const lastReadTimestamp = lastReadTimestamps[group.conversationId];
      const unreadCount = lastReadTimestamp
        ? groupMessages.filter(msg => 
            new Date(msg.timestamp) > new Date(lastReadTimestamp) &&
            msg.senderId !== currentUserId
          ).length
        : groupMessages.filter(msg => msg.senderId !== currentUserId).length;

      return {
        ...group,
        lastMessage,
        lastMessageTime: lastMessage ? new Date(lastMessage.timestamp) : new Date(0),
        unreadCount,
      };
    });

    return groupsWithLastMessage.sort((a, b) => {
      // Sort by last message time (most recent first)
      return b.lastMessageTime - a.lastMessageTime;
    });
  }, [groups, messages, currentUserId, lastReadTimestamps]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Groups</Typography>
        <IconButton 
          size="small" 
          onClick={onCreateGroupClick}
          color="primary"
          title="Create new group"
        >
          <AddIcon />
        </IconButton>
      </Box>
      <List sx={{ width: '100%', bgcolor: 'background.paper', flex: 1, overflow: 'auto' }}>
        {sortedGroups.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No groups yet. Create one to get started!
            </Typography>
          </Box>
        ) : (
          sortedGroups.map((group) => (
            <ListItem key={group.id} disablePadding>
              <ListItemButton
                selected={selectedGroupId === group.id}
                onClick={() => onSelectGroup(group.id)}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={group.unreadCount > 0 ? group.unreadCount : 0}
                    color="primary"
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiBadge-badge': {
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <UserAvatar name={group.name} />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={group.name}
                  secondary={
                    group.lastMessage ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {group.lastMessage.senderName}: {stripHtmlTags(group.lastMessage.message)}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );
}

export default GroupList;

